export const GOOGLE_GMAIL_READONLY_SCOPE =
  "https://www.googleapis.com/auth/gmail.readonly";

export const GOOGLE_GMAIL_MODIFY_SCOPE =
  "https://www.googleapis.com/auth/gmail.modify";

/** Gmail read + write (trash, drafts, archive) */
export const GOOGLE_GMAIL_WRITE_SCOPE = [
  GOOGLE_GMAIL_READONLY_SCOPE,
  GOOGLE_GMAIL_MODIFY_SCOPE,
].join(" ");

/** openid + profile — required for oauth2/v3/userinfo */
export const GOOGLE_IDENTITY_SCOPES = "openid profile";

/**
 * People API — contacts (work order S11f, gap fix). A DIFFERENT scope family
 * than Gmail: gmail.readonly does NOT grant contact reads, so already-
 * connected users must be taken through incremental re-consent
 * (include_granted_scopes=true) before the People sync can run.
 */
export const GOOGLE_CONTACTS_READONLY_SCOPE =
  "https://www.googleapis.com/auth/contacts.readonly";
export const GOOGLE_CONTACTS_SCOPE_FULL =
  "https://www.googleapis.com/auth/contacts";

/** Contacts connect scope string (read-only by default — S11f: new sources
 *  default to Read-only on first connect; the write scope is a per-source
 *  user upgrade, never the default). */
export const GOOGLE_CONTACTS_CONNECT_SCOPE = [
  GOOGLE_CONTACTS_READONLY_SCOPE,
  GOOGLE_IDENTITY_SCOPES,
].join(" ");

export const GOOGLE_DRIVE_SCOPES = [
  "https://www.googleapis.com/auth/drive.readonly",
  "https://www.googleapis.com/auth/drive.file",
  GOOGLE_IDENTITY_SCOPES,
].join(" ");

/** Gmail connect scope string (includes identity for userinfo) */
export const GOOGLE_GMAIL_SCOPE = [
  GOOGLE_GMAIL_READONLY_SCOPE,
  GOOGLE_GMAIL_MODIFY_SCOPE,
  GOOGLE_IDENTITY_SCOPES,
].join(" ");

export const GOOGLE_SCOPES = [
  "https://www.googleapis.com/auth/drive.readonly",
  "https://www.googleapis.com/auth/drive.file",
  GOOGLE_GMAIL_READONLY_SCOPE,
  GOOGLE_IDENTITY_SCOPES,
].join(" ");

export const STORAGE_KEY_TOKEN = "google_access_token";
export const STORAGE_KEY_PROFILE = "google_user_profile";
export const STORAGE_KEY_EXPIRY = "google_token_expiry";

const LEGACY_STORAGE_KEY_TOKEN = "google_drive_access_token";
const LEGACY_STORAGE_KEY_PROFILE = "google_drive_user_profile";
const LEGACY_STORAGE_KEY_EXPIRY = "google_drive_token_expiry";

export const GOOGLE_CONFIG = {
  clientId:
    import.meta.env.VITE_GOOGLE_CLIENT_ID ||
    "84317414482-qsc03mno25jmferhjb8rmvabeo43jqtb.apps.googleusercontent.com",
  // SECURITY: env-only — no hardcoded key fallback (leaked keys must be rotated + restricted by referrer).
  apiKey: import.meta.env.VITE_GOOGLE_API_KEY || "",
  appId: import.meta.env.VITE_GOOGLE_APP_ID || "84317414482",
} as const;

/** @deprecated Use GOOGLE_CONFIG */
export const GOOGLE_DRIVE_CONFIG = GOOGLE_CONFIG;

export type GoogleUserProfile = {
  email?: string;
  name?: string;
  picture?: string;
  sub?: string;
};

/** @deprecated Use GoogleUserProfile */
export type GoogleDriveUserProfile = GoogleUserProfile;

function migrateLegacyStorage() {
  try {
    const legacyToken = localStorage.getItem(LEGACY_STORAGE_KEY_TOKEN);
    if (legacyToken && !localStorage.getItem(STORAGE_KEY_TOKEN)) {
      localStorage.setItem(STORAGE_KEY_TOKEN, legacyToken);
      localStorage.removeItem(LEGACY_STORAGE_KEY_TOKEN);
    }
    const legacyProfile = localStorage.getItem(LEGACY_STORAGE_KEY_PROFILE);
    if (legacyProfile && !localStorage.getItem(STORAGE_KEY_PROFILE)) {
      localStorage.setItem(STORAGE_KEY_PROFILE, legacyProfile);
      localStorage.removeItem(LEGACY_STORAGE_KEY_PROFILE);
    }
    const legacyExpiry = localStorage.getItem(LEGACY_STORAGE_KEY_EXPIRY);
    if (legacyExpiry && !localStorage.getItem(STORAGE_KEY_EXPIRY)) {
      localStorage.setItem(STORAGE_KEY_EXPIRY, legacyExpiry);
      localStorage.removeItem(LEGACY_STORAGE_KEY_EXPIRY);
    }
  } catch {
    /* ignore */
  }
}

export function getStoredToken(): string | null {
  try {
    migrateLegacyStorage();
    return localStorage.getItem(STORAGE_KEY_TOKEN);
  } catch {
    return null;
  }
}

export function getStoredProfile(): GoogleUserProfile | null {
  try {
    migrateLegacyStorage();
    const profileStr = localStorage.getItem(STORAGE_KEY_PROFILE);
    return profileStr ? JSON.parse(profileStr) : null;
  } catch {
    return null;
  }
}

export function saveToken(token: string, profile: GoogleUserProfile | null) {
  try {
    localStorage.setItem(STORAGE_KEY_TOKEN, token);
    if (profile) {
      localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify(profile));
    }
    const expiryTime = Date.now() + 50 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY_EXPIRY, expiryTime.toString());
    localStorage.removeItem(LEGACY_STORAGE_KEY_TOKEN);
    localStorage.removeItem(LEGACY_STORAGE_KEY_PROFILE);
    localStorage.removeItem(LEGACY_STORAGE_KEY_EXPIRY);
  } catch (e) {
    console.warn("Failed to save token to localStorage:", e);
  }
}

export function clearToken() {
  try {
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_PROFILE);
    localStorage.removeItem(STORAGE_KEY_EXPIRY);
    localStorage.removeItem(LEGACY_STORAGE_KEY_TOKEN);
    localStorage.removeItem(LEGACY_STORAGE_KEY_PROFILE);
    localStorage.removeItem(LEGACY_STORAGE_KEY_EXPIRY);
  } catch (e) {
    console.warn("Failed to clear token from localStorage:", e);
  }
}

export function touchTokenExpiry() {
  try {
    const expiryTime = Date.now() + 50 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY_EXPIRY, expiryTime.toString());
  } catch {
    /* ignore */
  }
}

export function isStoredTokenExpired(): boolean {
  try {
    migrateLegacyStorage();
    const expiryStr = localStorage.getItem(STORAGE_KEY_EXPIRY);
    const expiryTime = expiryStr ? parseInt(expiryStr, 10) : 0;
    return expiryTime > 0 && expiryTime <= Date.now();
  } catch {
    return true;
  }
}

export class GoogleAuthError extends Error {
  constructor(message = "Google session expired. Please sign in again.") {
    super(message);
    this.name = "GoogleAuthError";
  }
}

/** @deprecated Use GoogleAuthError */
export class DriveAuthError extends GoogleAuthError {
  constructor(message = "Google Drive session expired. Please sign in again.") {
    super(message);
    this.name = "DriveAuthError";
  }
}

export type GoogleTokenInfo = {
  expires_in?: number;
  scope?: string;
  aud?: string;
  email?: string;
};

export async function fetchTokenInfo(
  token: string
): Promise<GoogleTokenInfo | null> {
  try {
    const response = await fetch(
      `https://oauth2.googleapis.com/tokeninfo?access_token=${encodeURIComponent(token)}`
    );
    if (!response.ok) return null;
    return (await response.json()) as GoogleTokenInfo;
  } catch {
    return null;
  }
}

export function parseTokenScopes(scopeField?: string): Set<string> {
  if (!scopeField) return new Set();
  return new Set(scopeField.trim().split(/\s+/).filter(Boolean));
}

export function tokenHasScope(tokenInfo: GoogleTokenInfo | null, scope: string): boolean {
  if (!tokenInfo?.scope) return false;
  return parseTokenScopes(tokenInfo.scope).has(scope);
}

export function tokenHasDriveScopes(tokenInfo: GoogleTokenInfo | null): boolean {
  return (
    tokenHasScope(tokenInfo, "https://www.googleapis.com/auth/drive.readonly") ||
    tokenHasScope(tokenInfo, "https://www.googleapis.com/auth/drive.file")
  );
}

export function tokenHasGmailScope(tokenInfo: GoogleTokenInfo | null): boolean {
  return tokenHasScope(tokenInfo, GOOGLE_GMAIL_READONLY_SCOPE);
}

export function tokenHasGmailModifyScope(tokenInfo: GoogleTokenInfo | null): boolean {
  return tokenHasScope(tokenInfo, GOOGLE_GMAIL_MODIFY_SCOPE);
}

export async function hasGmailModifyScope(token?: string | null): Promise<boolean> {
  const t = token ?? getStoredToken();
  if (!t) return false;
  const info = await fetchTokenInfo(t);
  return tokenHasGmailModifyScope(info);
}

/** Re-connect Gmail with modify scope (trash, drafts). */
export function gmailWriteConnectOptions(): { scope: string; prompt: "consent" } {
  return { scope: GOOGLE_GMAIL_SCOPE, prompt: "consent" };
}

export async function getTokenScopes(token: string): Promise<Set<string>> {
  const info = await fetchTokenInfo(token);
  return parseTokenScopes(info?.scope);
}

export async function validateToken(token: string): Promise<boolean> {
  const info = await fetchTokenInfo(token);
  return (info?.expires_in || 0) > 0;
}

export async function fetchUserProfile(
  token: string
): Promise<GoogleUserProfile | null> {
  try {
    const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      return (await res.json()) as GoogleUserProfile;
    }
  } catch {
    /* fall through to tokeninfo */
  }

  const info = await fetchTokenInfo(token);
  if (info?.email) {
    return { email: info.email };
  }
  return null;
}

/** Pick refresh scopes so silent refresh does not drop Drive or Gmail grants. */
export async function resolveRefreshScope(
  token: string,
  context: "drive" | "gmail" = "drive"
): Promise<string> {
  const info = await fetchTokenInfo(token);
  if (context === "gmail") {
    return tokenHasDriveScopes(info) ? GOOGLE_SCOPES : GOOGLE_GMAIL_SCOPE;
  }
  return tokenHasGmailScope(info) ? GOOGLE_SCOPES : GOOGLE_DRIVE_SCOPES;
}

export function loadGoogleScripts(): Promise<void> {
  const loadScript = (src: string) =>
    new Promise<void>((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = reject;
      document.body.appendChild(script);
    });

  return Promise.all([
    loadScript("https://apis.google.com/js/api.js"),
    loadScript("https://accounts.google.com/gsi/client"),
  ]).then(() => undefined);
}

export function loadPickerApi(): Promise<void> {
  return new Promise((resolve, reject) => {
    const gapi = (window as any).gapi;
    if (!gapi) {
      reject(new Error("gapi not loaded"));
      return;
    }
    gapi.load("picker", () => resolve());
  });
}

export type TokenClientCallback = (response: {
  access_token?: string;
  error?: string;
}) => void;

export function requestAccessToken(
  onResult: TokenClientCallback,
  options?: { prompt?: "" | "consent"; scope?: string }
): { cancel: () => void } {
  const tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
    client_id: GOOGLE_CONFIG.clientId,
    scope: options?.scope ?? GOOGLE_DRIVE_SCOPES,
    callback: onResult,
  });
  tokenClient.requestAccessToken(
    options?.prompt !== undefined ? { prompt: options.prompt } : undefined
  );
  return { cancel: () => {} };
}

/** GIS popup navigates the tab to gsi/transform in embedded browsers and never returns. */
export const GOOGLE_OAUTH_CALLBACK_PATH = "/google-oauth/callback";
const OAUTH_STATE_KEY = "google_oauth_state";
const OAUTH_RETURN_KEY = "google_oauth_return";
const OAUTH_PENDING_KEY = "google_oauth_pending";

export function googleOAuthRedirectUri(): string {
  // Must match an Authorized redirect URI on the OAuth client. This project’s
  // GIS client already allows the origin (e.g. http://localhost:5001), not a path.
  return window.location.origin;
}

export function googleOAuthReturnPath(): string {
  try {
    const path = sessionStorage.getItem(OAUTH_RETURN_KEY);
    if (path && path.startsWith("/") && !path.startsWith("//")) return path;
  } catch {
    /* ignore */
  }
  return "/plugins/gmail";
}

export type GoogleOAuthRedirectParse =
  | { ok: true; accessToken: string; expiresIn: number | null }
  | { ok: false; error: string };

export function parseGoogleOAuthCallback(
  hash: string,
  search: string,
  expectedState: string | null
): GoogleOAuthRedirectParse | null {
  const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
  const queryParams = new URLSearchParams(search.replace(/^\?/, ""));
  const error = hashParams.get("error") || queryParams.get("error");
  const errorDesc =
    hashParams.get("error_description") || queryParams.get("error_description") || "";
  const accessToken = hashParams.get("access_token");
  const state = hashParams.get("state") || queryParams.get("state");
  const expiresRaw = hashParams.get("expires_in");

  if (!error && !accessToken) return null;

  if (error) {
    const mismatch =
      error === "redirect_uri_mismatch" || /redirect_uri/i.test(errorDesc);
    return {
      ok: false,
      error: mismatch
        ? `Google rejected the redirect URI. In Google Cloud Console → APIs & Services → Credentials → your OAuth client, add this Authorized redirect URI exactly: ${typeof window !== "undefined" ? googleOAuthRedirectUri() : GOOGLE_OAUTH_CALLBACK_PATH}`
        : errorDesc || error,
    };
  }

  if (expectedState && state && state !== expectedState) {
    return { ok: false, error: "Google sign-in state mismatch. Please try again." };
  }
  if (!accessToken) {
    return { ok: false, error: "Google did not return an access token." };
  }
  const expiresIn = expiresRaw ? Number(expiresRaw) : null;
  return {
    ok: true,
    accessToken,
    expiresIn: Number.isFinite(expiresIn) ? expiresIn : null,
  };
}

/** Full-page OAuth so Cursor / embedded browsers return to the app after verification. */
export function buildGoogleOAuthAuthorizeUrl(options: {
  clientId: string;
  redirectUri: string;
  scope: string;
  state: string;
  prompt?: "" | "consent";
}): string {
  const params = new URLSearchParams({
    client_id: options.clientId,
    redirect_uri: options.redirectUri,
    response_type: "token",
    scope: options.scope,
    include_granted_scopes: "true",
    state: options.state,
  });
  if (options.prompt) params.set("prompt", options.prompt);
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

export function startGoogleOAuthRedirect(options?: {
  scope?: string;
  prompt?: "" | "consent";
  returnTo?: string;
}): void {
  const state =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const returnTo = options?.returnTo ?? `${window.location.pathname}${window.location.search}`;
  try {
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
    sessionStorage.setItem(
      OAUTH_RETURN_KEY,
      returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/plugins/gmail"
    );
  } catch {
    /* ignore */
  }
  window.location.assign(
    buildGoogleOAuthAuthorizeUrl({
      clientId: GOOGLE_CONFIG.clientId,
      redirectUri: googleOAuthRedirectUri(),
      scope: options?.scope ?? GOOGLE_DRIVE_SCOPES,
      state,
      prompt: options?.prompt,
    })
  );
}

export function consumeGoogleOAuthRedirect(): GoogleOAuthRedirectParse | null {
  if (typeof window === "undefined") return null;
  try {
    const pending = sessionStorage.getItem(OAUTH_PENDING_KEY);
    if (pending) return JSON.parse(pending) as GoogleOAuthRedirectParse;
  } catch {
    /* ignore */
  }
  let expected: string | null = null;
  try {
    expected = sessionStorage.getItem(OAUTH_STATE_KEY);
  } catch {
    expected = null;
  }
  const parsed = parseGoogleOAuthCallback(window.location.hash, window.location.search, expected);
  if (!parsed) return null;
  try {
    sessionStorage.setItem(OAUTH_PENDING_KEY, JSON.stringify(parsed));
    sessionStorage.removeItem(OAUTH_STATE_KEY);
  } catch {
    /* ignore */
  }
  const cleanPath = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState(null, "", cleanPath);
  return parsed;
}

export function clearGoogleOAuthPending(): void {
  try {
    sessionStorage.removeItem(OAUTH_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/** Silent refresh when user already granted consent. Returns null if unavailable. */
export function requestAccessTokenSilent(scope?: string): Promise<string | null> {
  return new Promise((resolve) => {
    if (!(window as any).google?.accounts?.oauth2) {
      resolve(null);
      return;
    }
    requestAccessToken(async (response) => {
      if (response.error || !response.access_token) {
        resolve(null);
        return;
      }
      const userInfo = await fetchUserProfile(response.access_token);
      saveToken(response.access_token, userInfo);
      resolve(response.access_token);
    }, { prompt: "", scope: scope ?? GOOGLE_DRIVE_SCOPES });
  });
}

/**
 * Returns a valid access token, refreshing silently when needed.
 * Throws GoogleAuthError when the session cannot be restored.
 */
function resolveAccessToken(currentToken?: string | null): string | null {
  const raw = currentToken ?? getStoredToken();
  return raw?.trim() ? raw : null;
}

export async function ensureGoogleAccessToken(
  currentToken?: string | null,
  options?: {
    refreshScope?: string;
    requiredScope?: string;
    preserveTokenOnRefreshFailure?: boolean;
  }
): Promise<string> {
  let token = resolveAccessToken(currentToken);
  if (!token) {
    throw new GoogleAuthError();
  }

  const refreshScope =
    options?.refreshScope ??
    (await resolveRefreshScope(token, "drive"));
  const needsRefresh =
    isStoredTokenExpired() || !(await validateToken(token));

  if (needsRefresh) {
    const refreshed = await requestAccessTokenSilent(refreshScope);
    if (!refreshed) {
      if (!options?.preserveTokenOnRefreshFailure) {
        clearToken();
      }
      throw new GoogleAuthError();
    }
    token = refreshed;
  }

  if (options?.requiredScope) {
    const info = await fetchTokenInfo(token);
    if (!tokenHasScope(info, options.requiredScope)) {
      throw new GoogleAuthError("Missing required Google permission. Please sign in again.");
    }
  }

  return token;
}

/** Scopes and prompt for incremental Gmail connect. */
export function gmailConnectOptions(hasExistingToken: boolean): {
  scope: string;
  prompt?: "consent";
} {
  if (!hasExistingToken) {
    return { scope: GOOGLE_GMAIL_SCOPE };
  }
  return { scope: GOOGLE_SCOPES, prompt: "consent" };
}

/** @deprecated Use ensureGoogleAccessToken */
export async function ensureDriveAccessToken(
  currentToken?: string | null
): Promise<string> {
  return ensureGoogleAccessToken(currentToken);
}

export async function revokeAndClear(token: string | null) {
  if (token && (window as any).google?.accounts?.oauth2?.revoke) {
    try {
      (window as any).google.accounts.oauth2.revoke(token);
    } catch {
      /* ignore */
    }
  }
  clearToken();
}

export async function restoreSession(): Promise<{
  token: string;
  profile: GoogleUserProfile | null;
} | null> {
  const savedToken = getStoredToken();
  if (!savedToken) return null;

  const isValid = await validateToken(savedToken);
  if (!isValid) {
    clearToken();
    return null;
  }

  if (isStoredTokenExpired()) {
    touchTokenExpiry();
  }

  return {
    token: savedToken,
    profile: getStoredProfile(),
  };
}
